import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, X, Loader2 } from 'lucide-react';
import { useApi } from '../hooks/useApi';

interface PatientResult {
  id: string;
  title: string;
  firstName: string;
  lastName: string;
  idNumber: string;
  phone: string;
  active: boolean;
}

interface SearchResults {
  patients: PatientResult[];
}

export function GlobalSearch() {
  const navigate = useNavigate();
  const { get } = useApi();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Keyboard shortcut: Cmd/Ctrl + K to open
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
      setResults(null);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!query.trim() || query.length < 2) {
      setResults(null);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await get<SearchResults>(`/search?q=${encodeURIComponent(query.trim())}`);
        if (res.success && res.data) {
          setResults(res.data);
        }
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [query, get]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = useCallback((path: string) => {
    setOpen(false);
    navigate(path);
  }, [navigate]);

  const hasResults = results && results.patients.length > 0;
  const noResults = results && !hasResults && query.length >= 2;

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
        title="Search (Ctrl+K)"
      >
        <Search className="h-3.5 w-3.5" />
        <span className="hidden md:inline">Search...</span>
        <kbd className="hidden md:inline-block ml-1 px-1.5 py-0.5 text-[10px] font-mono bg-gray-200 rounded">
          {navigator.platform.includes('Mac') ? '\u2318' : 'Ctrl'}K
        </kbd>
      </button>
    );
  }

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-40 bg-black/30" />

      {/* Search modal */}
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
        <div ref={containerRef} className="w-full max-w-xl mx-4 bg-white rounded-xl shadow-2xl overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
            <Search className="h-5 w-5 text-gray-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search patients..."
              className="flex-1 text-sm outline-none placeholder-gray-400"
              autoComplete="off"
            />
            {loading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
            <button onClick={() => setOpen(false)} className="p-1 text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Results */}
          <div className="max-h-[50vh] overflow-y-auto">
            {noResults && (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                No results for &ldquo;{query}&rdquo;
              </div>
            )}

            {hasResults && (
              <div className="py-2">
                {results!.patients.map(p => (
                  <button
                    key={p.id}
                    onClick={() => handleSelect(`/patients/${p.id}`)}
                    className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 text-left transition-colors"
                  >
                    <User className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {p.title} {p.firstName} {p.lastName}
                        {!p.active && (
                          <span className="ml-2 text-xs text-red-500">(inactive)</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        ID: {p.idNumber} {p.phone && `\u00B7 ${p.phone}`}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Empty state / hint */}
            {!results && !loading && (
              <div className="px-4 py-6 text-center text-sm text-gray-400">
                Type at least 2 characters to search
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
            <span>
              <kbd className="px-1 py-0.5 font-mono bg-gray-100 rounded">Esc</kbd> to close
            </span>
            <span>Search patients by name, ID, phone, or email</span>
          </div>
        </div>
      </div>
    </>
  );
}
